import type { SkillFileNode } from '@onefootprint/request-types/dashboard';

export type DropPosition = 'before' | 'after' | 'inside';

const parsePath = (path: string): number[] => {
  if (!path) return [];
  return path.split('.').map(segment => {
    const n = Number(segment);
    if (!Number.isInteger(n) || n < 0) throw new Error(`Invalid skills path segment: ${segment}`);
    return n;
  });
};

const childrenOf = (node: SkillFileNode): SkillFileNode[] => (node.kind === 'folder' ? node.children : []);

const cloneFolder = (node: SkillFileNode, nextChildren: SkillFileNode[]): SkillFileNode => {
  if (node.kind !== 'folder') return node;
  return { ...node, children: nextChildren };
};

export const getAtPath = (tree: SkillFileNode[], path: string): SkillFileNode | undefined => {
  const indices = parsePath(path);
  if (indices.length === 0) return undefined;
  let current: SkillFileNode | undefined = tree[indices[0]];
  for (let i = 1; i < indices.length && current; i++) {
    current = childrenOf(current)[indices[i]];
  }
  return current;
};

export const updateAtPath = (
  tree: SkillFileNode[],
  path: string,
  update: (node: SkillFileNode) => SkillFileNode,
): SkillFileNode[] => {
  const indices = parsePath(path);
  if (indices.length === 0) return tree;

  const recurse = (nodes: SkillFileNode[], depth: number): SkillFileNode[] => {
    const index = indices[depth];
    if (index < 0 || index >= nodes.length) return nodes;
    const node = nodes[index];
    const next = depth === indices.length - 1 ? update(node) : cloneFolder(node, recurse(childrenOf(node), depth + 1));
    if (next === node) return nodes;
    const copy = nodes.slice();
    copy[index] = next;
    return copy;
  };

  return recurse(tree, 0);
};

export const removeAtPath = (tree: SkillFileNode[], path: string): SkillFileNode[] => {
  const indices = parsePath(path);
  if (indices.length === 0) return tree;

  const recurse = (nodes: SkillFileNode[], depth: number): SkillFileNode[] => {
    const index = indices[depth];
    if (index < 0 || index >= nodes.length) return nodes;
    if (depth === indices.length - 1) {
      const copy = nodes.slice();
      copy.splice(index, 1);
      return copy;
    }
    const node = nodes[index];
    const nextChildren = recurse(childrenOf(node), depth + 1);
    if (nextChildren === childrenOf(node)) return nodes;
    const copy = nodes.slice();
    copy[index] = cloneFolder(node, nextChildren);
    return copy;
  };

  return recurse(tree, 0);
};

export const appendToFolderAtPath = (
  tree: SkillFileNode[],
  folderPath: string,
  toAppend: SkillFileNode[],
): SkillFileNode[] => {
  if (toAppend.length === 0) return tree;
  return updateAtPath(tree, folderPath, node => {
    if (node.kind !== 'folder') return node;
    return { ...node, children: [...node.children, ...toAppend] };
  });
};

export const insertAtPath = (
  tree: SkillFileNode[],
  path: string,
  position: DropPosition,
  toInsert: SkillFileNode[],
): SkillFileNode[] => {
  if (toInsert.length === 0) return tree;
  if (position === 'inside') return appendToFolderAtPath(tree, path, toInsert);

  const indices = parsePath(path);
  if (indices.length === 0) return tree;

  const recurse = (nodes: SkillFileNode[], depth: number): SkillFileNode[] => {
    const index = indices[depth];
    if (depth === indices.length - 1) {
      const insertAt = position === 'before' ? index : index + 1;
      const copy = nodes.slice();
      copy.splice(insertAt, 0, ...toInsert);
      return copy;
    }
    if (index < 0 || index >= nodes.length) return nodes;
    const node = nodes[index];
    const nextChildren = recurse(childrenOf(node), depth + 1);
    if (nextChildren === childrenOf(node)) return nodes;
    const copy = nodes.slice();
    copy[index] = cloneFolder(node, nextChildren);
    return copy;
  };

  return recurse(tree, 0);
};

export const appendToRoot = (tree: SkillFileNode[], nodes: SkillFileNode[]): SkillFileNode[] =>
  nodes.length === 0 ? tree : [...tree, ...nodes];

/**
 * Move a node from `fromPath` to `toPath`+`position`. Removes first, then
 * inserts — so callers don't have to reason about path shifts caused by the
 * insert. When the move is a no-op (dropping a node onto itself or its
 * adjacent slot) returns the original tree.
 */
export const moveNode = (
  tree: SkillFileNode[],
  fromPath: string,
  toPath: string,
  position: DropPosition,
): SkillFileNode[] => {
  const node = getAtPath(tree, fromPath);
  if (!node) return tree;
  if (fromPath === toPath) return tree;
  const without = removeAtPath(tree, fromPath);
  const shiftedTo = shiftPathAfterRemoval(toPath, fromPath);
  if (shiftedTo === null) return tree;
  return insertAtPath(without, shiftedTo, position, [node]);
};

/**
 * After removing the node at `removedPath`, return where `targetPath` now
 * lives. Returns null if `targetPath` was the removed node itself.
 */
export const shiftPathAfterRemoval = (targetPath: string, removedPath: string): string | null => {
  const target = parsePath(targetPath);
  const removed = parsePath(removedPath);

  // target lives in a different subtree — unaffected.
  for (let i = 0; i < removed.length - 1; i++) {
    if (target[i] !== removed[i]) return targetPath;
    if (i === target.length - 1) return targetPath;
  }

  const sharedDepth = removed.length - 1;
  if (target.length <= sharedDepth) return targetPath;

  const removedIndex = removed[sharedDepth];
  const targetIndex = target[sharedDepth];

  if (targetIndex === removedIndex && target.length === removed.length) return null;
  if (targetIndex < removedIndex) return targetPath;
  const next = target.slice();
  next[sharedDepth] = targetIndex - 1;
  return next.join('.');
};

export const isDescendantPath = (parentPath: string, candidatePath: string): boolean => {
  if (!parentPath) return false;
  if (parentPath === candidatePath) return false;
  return candidatePath.startsWith(`${parentPath}.`);
};
